<script setup lang="ts">
import { computed } from 'vue'
import type { BookPageConfig } from '../configs/types'
import { t } from '@/i18n/utils'
import { formatCompactNumber } from '@/utils'

interface Props {
  book: GetContentGetcontentdetailResponse['data']
  config: BookPageConfig
  coverUrl?: string
  introduce?: string
  paragraphs: string[]
  popupTOC: boolean
  bannerShow: boolean
  isInShelf?: boolean
  shelfLoading?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (event: 'update:popup-t-o-c', value: boolean): void
  (event: 'goto-tags', tagId: string): void
  (event: 'open-toc'): void
  (event: 'read'): void
  (event: 'back'): void
  (event: 'toggle-shelf'): void
}>()

const tagList = computed(() => (props.book.tagList || []).filter((t): t is { name: string } => !!t.name))
const startReadingBg = computed(() => props.config.theme.startReadingBg || 'var(--btn-primary-bg)')

// 创建可写的 computed 用于 v-model 双向绑定
const popupTOCModel = computed({
  get: () => props.popupTOC,
  set: value => emit('update:popup-t-o-c', value),
})
</script>

<template>
  <div class="min-h-screen" style="background: #F6F7F9;">
    <!-- Novelarc 封面区域 - 高度260px -->
    <div
      class="h-230px relative"
      :class="{ 'mt-60px': bannerShow }"
    >
      <!-- 下层背景：书封高斯模糊 -->
      <div
        class="w-full h-full absolute top-0 left-0 bg-cover bg-center bg-no-repeat"
        :style="`background-image: url(${coverUrl})`"
      />
      <!-- 模糊效果 -->
      <div
        class="w-full h-full absolute top-0 left-0"
        style="backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);"
      />
      <!-- 上层背景遮罩 -->
      <div
        class="w-full h-full absolute top-0 left-0"
        style="background: rgba(26,30,33,0.5);"
      />

      <!-- 返回按钮 -->
      <div
        class="absolute top-12px left-12px z-10 cursor-pointer"
        @click="emit('back')"
      >
        <i-icon-park-outline-left class="text-24px text-white" />
      </div>

      <!-- 书籍信息：左边书封 + 右边文字，居中对齐 -->
      <div class="absolute bottom-36px left-16px right-16px z-10 flex items-center">
        <!-- 书封 -->
        <img
          class="w-96px h-128px rounded-8px flex-shrink-0 object-cover"
          :src="coverUrl"
          alt="Book Cover"
        >
        <!-- 文字信息 -->
        <div class="ml-16px flex-1 min-w-0">
          <!-- 书名 -->
          <p class="font-bold text-16px text-white line-clamp-3">
            {{ book.contentName }}
          </p>
          <!-- 作者 -->
          <div class="mt-12px flex items-center">
            <img
              src="/images/novelarc/book/icon-author.png"
              class="w-13px h-13px flex-shrink-0"
              alt="author"
            >
            <span class="ml-8px text-12px text-white lh-12px truncate">
              {{ book.authorInfo?.author }}
            </span>
          </div>
          <!-- 书状态（带icon） -->
          <div class="mt-12px flex items-center">
            <img
              src="/images/novelarc/book/icon-completed.png"
              class="w-13px h-13px flex-shrink-0"
              alt="status"
            >
            <span class="ml-8px text-12px text-white lh-12px">
              {{ book.finishStatus ? config.texts.completed : config.texts.ongoing }}
            </span>
          </div>
          <!-- 阅读量（带icon） -->
          <div v-if="book.viewUsers" class="mt-12px flex items-center text-white">
            <img
              src="/images/novelarc/book/icon-view-count.svg"
              class="w-13px h-13px flex-shrink-0"
              alt="view count"
            >
            <span class="ml-8px text-12px lh-12px">
              {{ formatCompactNumber(book.viewUsers) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 中间部分：书籍简介（向上偏移20px，圆角15px） -->
    <div
      class="bg-white px-16px pt-16px relative z-1 -mt-20px"
      style="border-radius: 15px 15px 0 0;"
    >
      <!-- Introduction 标题 -->
      <h2 class="font-bold text-16px lh-16px" style="color: #32353A;">
        {{ config.texts.bookIntroduce }}
      </h2>

      <!-- 标签 -->
      <div class="mt-12px">
        <BizShowTags :tags="tagList" :scrollable="config.theme.tagsScrollable" />
      </div>

      <!-- 简介文本 -->
      <div class="mt-12px text-14px">
        <BasicExpandableText
          :text="book.introduce"
          :line-clamp="6"
          text-class="text-14px lh-22px text-[#6A6E7A]"
          more-class="text-n-primary cursor-pointer text-14px"
          more-bg-color="white"
        />
      </div>

      <!-- 分割线 -->
      <div class="mt-16px mx-auto h-1px w-345px" style="background: #F3F5FA;" />

      <!-- Chapters -->
      <div
        class="h-44px flex items-center justify-between cursor-pointer"
        @click="emit('open-toc')"
      >
        <span class="font-bold text-16px lh-14px" style="color: #32353A;">
          {{ book.lastChapter?.sequenceNo || 0 }} {{ config.texts.chapters }}
        </span>
        <img
          src="/images/novelarc/icon-more.png"
          class="w-6px h-10px"
          alt="more"
        >
      </div>
    </div>

    <!-- 间距背景 -->
    <div class="h-10px" style="background: #F6F7F9;" />

    <!-- 第三部分：第一章内容 -->
    <div class="bg-white px-16px pt-16px pb-20px">
      <!-- 引导语 -->
      <div v-if="introduce" class="mb-16px text-14px" :style="{ color: config.theme.leadTextColor || '#6A6E7A' }">
        <p v-html="introduce" />
      </div>

      <!-- 章节标题 -->
      <h3 class="font-bold text-16px" style="color: #32353A;">
        {{ book.firstChapter?.chapterName }}
      </h3>

      <!-- 章节内容 -->
      <div class="mt-12px">
        <p
          v-for="(item, index) in paragraphs.slice(0, 16)"
          :key="index"
          class="mt-12px text-14px lh-24px"
          style="color: #6A6E7A;"
        >
          {{ item }}
        </p>
      </div>
    </div>

    <!-- TOC 弹窗 -->
    <BizPopupBookTOC
      v-if="book && book.firstChapterContentUrl"
      v-model="popupTOCModel"
      :book="book"
      :content-id="book.id"
      :chapter-id="book.firstChapter?.id"
      :lock-icon-color="config.theme.tocLockColor"
    />

    <!-- 底部固定按钮 -->
    <BasicFixedBottom class="shadow-[0px_-2px_7px_0px_rgba(99,100,100,0.1)]">
      <div class="w-full flex items-center px-16px pt-12px pb-0 bg-white gap-15px">
        <div
          class="flex flex-col items-center justify-center min-w-50px py-2px cursor-pointer"
          :class="{ 'opacity-50': shelfLoading }"
          :style="{ color: isInShelf ? 'var(--primary-color)' : '#65686F' }"
          @click="emit('toggle-shelf')"
        >
          <i-assets-book-added-shelf v-if="isInShelf" class="w-22px h-22px pointer-events-none" />
          <i-assets-book-add-shelf v-else class="w-22px h-22px pointer-events-none" />
          <p class="mt-2px text-10px lh-11px">
            {{ isInShelf ? t('BookshelfAdded') : t('Bookshelf') }}
          </p>
        </div>
        <a
          class="flex-1 h-45px lh-45px text-center text-15px font-bold rounded-22.5px cursor-pointer text-white"
          :style="{ background: startReadingBg }"
          @click="emit('read')"
        >
          {{ !book.readFlag ? config.texts.startReading : config.texts.continueReading }}
        </a>
      </div>
    </BasicFixedBottom>
  </div>
</template>
